import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';

const THEME_COOKIE_PATH = '/';
const THEME_COOKIE_DAYS = 365;

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  public endpoint: string = `${environment.apiUrl}/products`;
  public themeData$: State<string>;
  public theme$: Observable<string>;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _cookies: CookieService
    ) {
      this.themeData$ = new State('');
      this.theme$ = select$(this.themeData$, data => data);
     }

  init() {
    let theme = this.readTheme();
    if (theme !== 'dark-theme' && theme !== 'light-theme') {
      theme = 'light-theme';
    }
    return this.onSetTheme(theme);
  }

  onToggleTheme(theme){
    theme = theme == 'dark-theme' ? 'light-theme' : 'dark-theme';
    this.writeTheme(theme);
    this.themeData$.next(theme);
    this.onToggleClass(theme);
  }

  onSetTheme(theme){
    this.writeTheme(theme);
    this.themeData$.next(theme);
    this.onToggleClass(theme);
  }

  /**
   * Reads the saved theme. Older versions wrote the cookie without a path, so
   * the browser scoped it to the directory of the page and several "theme"
   * cookies may be sent at once. The browser lists the most specific path
   * first, so the last entry is the site-root cookie (or the least specific
   * stray when no root cookie exists yet).
   */
  private readTheme(): string {
    const matches = (this.document.cookie || '').match(/(?:^|;\s*)theme=([^;]*)/g);
    if (!matches || !matches.length) {
      return '';
    }
    const last = matches[matches.length - 1];
    return decodeURIComponent(last.substring(last.indexOf('theme=') + 6));
  }

  /** Writes the single site-wide theme cookie and removes path-scoped strays. */
  private writeTheme(theme: string): void {
    this.clearStrayCookies();
    this._cookies.set('theme', theme, {
      expires: THEME_COOKIE_DAYS,
      path: THEME_COOKIE_PATH,
      sameSite: 'Lax'
    });
  }

  /**
   * Expires "theme" cookies at every ancestor directory of the current page
   * (for /a/b/c: /a/b, /a, /a/b/c). Only cookies whose path is a prefix of the
   * current path are sent to this page, so these are exactly the ones that can
   * shadow the root cookie here. Strays at unrelated directories are cleared
   * the same way when the user loads a page under them.
   */
  private clearStrayCookies(): void {
    const segments = (this.document.location.pathname || '/').split('/').filter(s => s);
    let path = '';
    for (const segment of segments) {
      path += '/' + segment;
      this._cookies.delete('theme', path);
    }
  }

  onToggleClass(theme){
    const htmlTag = this.document.getElementById('app-theme');
    htmlTag.removeAttribute('class');
    htmlTag.className = theme;
  }
}
